import { ApiError, call, type Item } from './api';

// A signed-out buyer's list stays on their phone (José, 22 Oct 2026), in this browser's storage. Signing in moves it
// into the account (POST /api/find/list/merge) and empties it here.

const KEY = 'grailfind-list';
const CHANGE = 'grailfind:phone-list';

// Goes up on every change to My list, the phone's or the account's, so the header's count reads it again.
let version = 0;
export const listVersion = () => version;

/** Tells the page My list changed (an add or a remove on the account). */
export function bumpList() {
  version += 1;
  window.dispatchEvent(new Event(CHANGE));
}

export type PhoneCard = Pick<Item, 'id' | 'name' | 'grade' | 'price' | 'cents' | 'currency' | 'symbol' | 'picture' | 'shop' | 'show' | 'grader' | 'grade_value'> & {
  added: number;
};

export function readList(): PhoneCard[] {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]') as unknown;
    return Array.isArray(list) ? list.filter((x): x is PhoneCard => !!x && typeof (x as PhoneCard).id === 'string') : [];
  } catch {
    return [];
  }
}

/** False when this browser keeps nothing (a private window on some phones). */
export function writeList(list: PhoneCard[]): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
    bumpList();
    return true;
  } catch {
    return false;
  }
}

export function onListChange(onChange: () => void) {
  const storage = (e: StorageEvent) => {
    if (e.key === KEY) {
      version += 1;
      onChange();
    }
  };
  window.addEventListener(CHANGE, onChange);
  window.addEventListener('storage', storage);
  return () => {
    window.removeEventListener(CHANGE, onChange);
    window.removeEventListener('storage', storage);
  };
}

/** What the phone keeps of a card: what its page shows, nothing more. */
export function phoneCard(item: Item, now = Date.now()): PhoneCard {
  const { id, name, grade, price, cents, currency, symbol, picture, shop, show, grader, grade_value } = item;
  return { id, name, grade, price, cents, currency, symbol, picture, shop, show, grader, grade_value, added: now };
}

// The account's cap (Grail.Buyers' @max_items): one merge sends no more ids than that.
const MERGE_MAX = 200;

/**
 * Moves the phone's cards into the signed-in account, then takes them off the phone, except the cards that did not fit
 * on a full list (redteam #251 L1): those stay here, with any past the first 200. How many stay; null when the move did
 * not go (no signal at the show): the cards stay here, and My list moves them on its next open.
 */
export async function mergePhoneList(): Promise<number | null> {
  const ids = readList()
    .map((x) => x.id)
    .slice(0, MERGE_MAX);
  if (!ids.length) return 0;
  let full: string[];
  try {
    full = (await call<{ full?: string[] }>('/api/find/list/merge', { method: 'POST', body: { ids } })).full ?? [];
  } catch {
    return null;
  }
  const left = readList().filter((x) => !ids.includes(x.id) || full.includes(x.id));
  writeList(left);
  return left.length;
}

/** Adds the card unless it is there already. False when the browser cannot keep it. */
export function addToPhone(item: Item): boolean {
  const list = readList();
  if (list.some((x) => x.id === item.id)) return true;
  return writeList([...list, phoneCard(item)]);
}

// Cards on their way from the old GrailFind pages, which kept a signed-out list in the API address's own browser
// storage: once the website takes over, a sticker's old address sends the browser here with the cards' ids after
// #from= (GrailWeb.ItemPageController's @move_script). Each id is looked up before its card goes on the list, so a
// link cannot put a made-up card there. Any link can carry #from=, so the ids wait in OFFERED until the buyer answers
// "Add N cards from your old GrailFind list?" (redteam #260 L1): Add sends them to the lookups (MOVING), No drops
// them. MOVED keeps every id answered: the old pages send their whole list each time, and a card the buyer removed or
// said No to must not come back with the next sticker.
const OFFERED = 'grailfind-offered';
const MOVING = 'grailfind-moving';
const MOVED = 'grailfind-moved';
const PUBLIC_ID = /^[a-z2-7]{26}$/;

function readIds(key: string): string[] {
  try {
    const ids = JSON.parse(localStorage.getItem(key) || '[]') as unknown;
    return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string' && PUBLIC_ID.test(x)) : [];
  } catch {
    return [];
  }
}

function writeIds(key: string, ids: string[]) {
  try {
    if (ids.length) localStorage.setItem(key, JSON.stringify(ids));
    else localStorage.removeItem(key);
  } catch {
    // A browser that keeps nothing: the cards stay on the old pages' list.
  }
}

/** How many cards from the old pages are still to be looked up. */
export const movingCount = () => readIds(MOVING).length;

/** How many cards from the old pages wait for the buyer's Add or No. */
export const offeredCount = () => readIds(OFFERED).length;

/**
 * Takes the ids after #from= in an address, each id once ever, to wait for the buyer's answer: at most MERGE_MAX wait.
 * True when the address has that part: the page then takes it out of the address.
 */
export function takeMoved(hash: string): boolean {
  const m = /^#from=([^#]*)$/.exec(hash);
  if (!m) return false;
  const offered = readIds(OFFERED);
  const seen = new Set([...readIds(MOVED), ...offered]);
  const fresh: string[] = [];
  for (const id of m[1]!.toLowerCase().split(',')) {
    if (offered.length + fresh.length >= MERGE_MAX || !PUBLIC_ID.test(id) || seen.has(id)) continue;
    seen.add(id);
    fresh.push(id);
  }
  if (fresh.length) {
    writeIds(OFFERED, [...offered, ...fresh]);
    bumpList();
  }
  return true;
}

/**
 * The buyer's answer to the cards waiting from the old pages: Add sends them to the lookups (`loadMoving` puts each one
 * on the list), No drops them. Either way they count as taken, so the next sticker does not ask about them again.
 */
export function answerOffer(add: boolean) {
  const ids = readIds(OFFERED);
  if (!ids.length) return;
  writeIds(MOVED, [...readIds(MOVED), ...ids]);
  if (add) writeIds(MOVING, [...readIds(MOVING), ...ids]);
  writeIds(OFFERED, []);
  bumpList();
}

let looking = false;

/**
 * Looks up each card on its way from the old pages and puts it on this phone's list, in the old list's order. A card
 * the website no longer has (sold out, taken off stock or unknown) is dropped. No signal, or any other failed answer,
 * stops the lookups: the rest wait for the next page or for the signal to come back.
 */
export async function loadMoving(): Promise<void> {
  if (looking) return;
  looking = true;
  try {
    for (const id of readIds(MOVING)) {
      try {
        if (!addToPhone(await call<Item>(`/api/find/items/${id}`))) return;
      } catch (e) {
        if (!(e instanceof ApiError) || e.status !== 404) return;
      }
      writeIds(MOVING, readIds(MOVING).filter((x) => x !== id));
      bumpList();
    }
  } finally {
    looking = false;
  }
}
